// Resolution of a GlobalSearchModal selection to a LinkedIn URL, shared by the compose
// "Write message" flow (useComposeWriteMessage) and the confirm-selection mode
// (useConfirmSelection). Pure functions: the RTK Query triggers are injected as plain
// async callbacks so callers (and tests) decide how lookups are performed.

import { ListPersonGlobalSearchInfoDTO } from "@/pages/Private/redux/search/search.schema";

import { SelectedLinkedInItem } from "./singleSelect";

export type GetContactDetail = (
	contactId: number
) => Promise<{ linkedinUrl?: string | null } | undefined>;

export type GetCompanyEnrichPerson = (
	companyEnrichId: number
) => Promise<{ socials?: { linkedin_url?: string | null } | null } | undefined>;

// Resolve a selected contact (existing prospect) to their LinkedIn URL: prefer the URL already
// in the search slice, fall back to the contact prospect detail (the slice is empty when the
// contact was selected via the By URL tab).
export async function resolveContactLinkedinUrl(
	contactId: number,
	searchContacts: ListPersonGlobalSearchInfoDTO[],
	getContactDetail: GetContactDetail
): Promise<string | undefined> {
	const sliceUrl = searchContacts.find((c) => c.prospect?.id === contactId)?.prospect?.linkedinUrl;

	if (sliceUrl) {
		return sliceUrl;
	}

	const detail = await getContactDetail(contactId);

	return detail?.linkedinUrl ?? undefined;
}

// Resolve a selected LinkedIn item to its URL: URL-resolved prospects (By URL tab) already carry
// one; companyEnrich preview people carry none client-side, so resolve it from the person detail.
export async function resolveLinkedInItemUrl(
	item: SelectedLinkedInItem,
	getCompanyEnrichPerson: GetCompanyEnrichPerson
): Promise<string | undefined> {
	if (item.linkedinUrl) {
		return item.linkedinUrl;
	}

	if (item.companyEnrichId !== undefined) {
		const detail = await getCompanyEnrichPerson(item.companyEnrichId);
		const resolvedUrl = detail?.socials?.linkedin_url;

		if (resolvedUrl) {
			return resolvedUrl;
		}
	}

	return undefined;
}
